import { Country } from 'src/api/zrm';
import type { MapPlace } from 'src/tv/live/places';
import { place } from 'src/tv/live/places';

/**
 * Where a zip code lands on the map, from its first two digits only: a region's main town, not the address.
 * Each row covers its prefix up to the next row's. Hand-made from the SE and NO postal code areas.
 */
type Row = [prefix: number, name: string, lon: number, lat: number];

const SE: Row[] = [
  [10, 'Stockholm', 18.07, 59.33],
  [20, 'Malmö', 13.0, 55.6],
  [24, 'Lund', 13.19, 55.7],
  [25, 'Helsingborg', 12.69, 56.05],
  [26, 'Ängelholm', 12.86, 56.24],
  [27, 'Ystad', 13.82, 55.43],
  [28, 'Hässleholm', 13.77, 56.16],
  [29, 'Kristianstad', 14.16, 56.03],
  [30, 'Halmstad', 12.86, 56.67],
  [31, 'Varberg', 12.25, 57.1],
  [33, 'Värnamo', 14.04, 57.18],
  [34, 'Ljungby', 13.94, 56.83],
  [35, 'Växjö', 14.81, 56.88],
  [37, 'Karlskrona', 15.59, 56.16],
  [38, 'Kalmar', 16.36, 56.66],
  [40, 'Göteborg', 11.97, 57.71],
  [43, 'Kungsbacka', 12.07, 57.49],
  [44, 'Alingsås', 12.53, 57.93],
  [45, 'Uddevalla', 11.94, 58.35],
  [46, 'Trollhättan', 12.29, 58.28],
  [47, 'Stenungsund', 11.82, 58.07],
  [50, 'Borås', 12.94, 57.72],
  [52, 'Falköping', 13.55, 58.17],
  [53, 'Lidköping', 13.16, 58.5],
  [54, 'Skövde', 13.85, 58.39],
  [55, 'Jönköping', 14.16, 57.78],
  [57, 'Nässjö', 14.7, 57.65],
  [58, 'Linköping', 15.62, 58.41],
  [59, 'Motala', 15.04, 58.54],
  [60, 'Norrköping', 16.19, 58.59],
  [61, 'Nyköping', 17.01, 58.75],
  [62, 'Visby', 18.29, 57.64],
  [63, 'Eskilstuna', 16.51, 59.37],
  [64, 'Katrineholm', 16.21, 59.0],
  [65, 'Karlstad', 13.5, 59.38],
  [66, 'Säffle', 12.93, 59.13],
  [67, 'Arvika', 12.59, 59.65],
  [68, 'Hagfors', 13.69, 60.03],
  [69, 'Karlskoga', 14.52, 59.33],
  [70, 'Örebro', 15.21, 59.27],
  [71, 'Lindesberg', 15.22, 59.59],
  [72, 'Västerås', 16.54, 59.61],
  [73, 'Sala', 16.6, 59.92],
  [74, 'Enköping', 17.08, 59.64],
  [75, 'Uppsala', 17.64, 59.86],
  [76, 'Norrtälje', 18.7, 59.76],
  [77, 'Ludvika', 15.19, 60.15],
  [78, 'Borlänge', 15.42, 60.48],
  [79, 'Mora', 14.54, 61.0],
  [80, 'Gävle', 17.14, 60.67],
  [81, 'Sandviken', 16.78, 60.62],
  [82, 'Hudiksvall', 17.1, 61.73],
  [83, 'Östersund', 14.64, 63.18],
  [84, 'Sveg', 14.36, 62.03],
  [85, 'Sundsvall', 17.31, 62.39],
  [87, 'Härnösand', 17.94, 62.63],
  [88, 'Sollefteå', 17.27, 63.17],
  [89, 'Örnsköldsvik', 18.71, 63.29],
  [90, 'Umeå', 20.26, 63.83],
  [92, 'Lycksele', 18.67, 64.6],
  [93, 'Skellefteå', 20.95, 64.75],
  [94, 'Piteå', 21.48, 65.32],
  [95, 'Luleå', 22.15, 65.58],
  [96, 'Boden', 21.69, 65.83],
  [97, 'Luleå', 22.15, 65.58],
  [98, 'Kiruna', 20.23, 67.86],
];

const NO: Row[] = [
  [0, 'Oslo', 10.75, 59.91],
  [13, 'Bærum', 10.52, 59.89],
  [14, 'Ski', 10.84, 59.72],
  [15, 'Moss', 10.66, 59.43],
  [16, 'Fredrikstad', 10.93, 59.22],
  [17, 'Sarpsborg', 11.11, 59.28],
  [18, 'Askim', 11.16, 59.58],
  [19, 'Lillestrøm', 11.05, 59.96],
  [20, 'Jessheim', 11.17, 60.14],
  [22, 'Kongsvinger', 12.0, 60.19],
  [23, 'Hamar', 11.07, 60.79],
  [24, 'Elverum', 11.56, 60.88],
  [25, 'Tynset', 10.78, 62.27],
  [26, 'Lillehammer', 10.47, 61.12],
  [28, 'Gjøvik', 10.69, 60.8],
  [29, 'Fagernes', 9.23, 60.99],
  [30, 'Drammen', 10.2, 59.74],
  [31, 'Tønsberg', 10.41, 59.27],
  [32, 'Sandefjord', 10.22, 59.13],
  [33, 'Hokksund', 9.91, 59.77],
  [35, 'Hønefoss', 10.26, 60.17],
  [36, 'Kongsberg', 9.65, 59.67],
  [37, 'Skien', 9.61, 59.21],
  [38, 'Notodden', 9.26, 59.56],
  [39, 'Porsgrunn', 9.66, 59.14],
  [40, 'Stavanger', 5.73, 58.97],
  [43, 'Sandnes', 5.74, 58.85],
  [44, 'Flekkefjord', 6.66, 58.3],
  [45, 'Mandal', 7.46, 58.03],
  [46, 'Kristiansand', 7.99, 58.15],
  [48, 'Arendal', 8.77, 58.46],
  [49, 'Tvedestrand', 8.93, 58.62],
  [50, 'Bergen', 5.32, 60.39],
  [54, 'Stord', 5.5, 59.78],
  [55, 'Haugesund', 5.27, 59.41],
  [56, 'Odda', 6.54, 60.07],
  [57, 'Voss', 6.42, 60.63],
  [58, 'Knarvik', 5.33, 60.55],
  [60, 'Ålesund', 6.15, 62.47],
  [61, 'Volda', 6.07, 62.15],
  [62, 'Molde', 7.16, 62.74],
  [65, 'Kristiansund', 7.73, 63.11],
  [66, 'Sunndalsøra', 8.55, 62.67],
  [67, 'Førde', 5.85, 61.45],
  [68, 'Sogndal', 7.1, 61.23],
  [69, 'Førde', 5.85, 61.45],
  [70, 'Trondheim', 10.4, 63.43],
  [72, 'Orkanger', 9.85, 63.3],
  [73, 'Røros', 11.39, 62.57],
  [75, 'Stjørdal', 10.92, 63.47],
  [76, 'Levanger', 11.3, 63.75],
  [77, 'Steinkjer', 11.5, 64.01],
  [78, 'Namsos', 11.5, 64.47],
  [80, 'Bodø', 14.4, 67.28],
  [82, 'Fauske', 15.39, 67.26],
  [83, 'Svolvær', 14.57, 68.23],
  [84, 'Sortland', 15.41, 68.7],
  [85, 'Narvik', 17.43, 68.44],
  [86, 'Mo i Rana', 14.14, 66.31],
  [87, 'Mosjøen', 13.19, 65.84],
  [88, 'Sandnessjøen', 12.63, 66.02],
  [89, 'Brønnøysund', 12.21, 65.47],
  [90, 'Tromsø', 18.96, 69.65],
  [92, 'Finnsnes', 17.98, 69.23],
  [93, 'Harstad', 16.54, 68.8],
  [95, 'Alta', 23.27, 69.97],
  [96, 'Hammerfest', 23.68, 70.66],
  [97, 'Lakselv', 24.96, 70.05],
  [98, 'Vadsø', 29.75, 70.07],
  [99, 'Kirkenes', 30.05, 69.73],
];

const ROWS: Record<Country, Row[]> = { [Country.SE]: SE, [Country.NO]: NO };

/** The region of a zip code's first two digits (all zrm sends), or null when they are not a SE or NO region. */
export const placeForZipPrefix = (country: Country, prefix: string): MapPlace | null => {
  if (!/^\d{2}$/.test(prefix)) return null;

  const row = ROWS[country].filter(([from]) => from <= Number(prefix)).at(-1);

  return row ? place(row[1], row[2], row[3]) : null;
};
